/**
 * The graph (Neo4j): the project's labels and relationship types with how many there are; each opens in Query with a
 * first Cypher query (always filtered on the project, as every graph query here is).
 */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { EmptyState, ErrorState, Loading, errorText } from './DataCommon';
import { getGraphLabels } from '../../services/DataService';
import { formatCount } from '../../utils/dataModel';

const quote = (name) => `\`${String(name).replace(/`/g, '``')}\``;

export default function GraphView({ projectId, label = null, onOpenQuery }) {
  const [answer, setAnswer] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    setError(null);
    try { setAnswer(await getGraphLabels(projectId)); } catch (e) { setError(errorText(e)); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const labels = answer?.labels || [];
  const relationships = answer?.relationships || [];
  return (
    <div className="data-view" data-testid="graph-view">
      <div className="data-view-head">
        <h1>{label || 'Graph'}</h1>
        {answer && <span className="data-pill">{formatCount(labels.length)} label{labels.length === 1 ? '' : 's'} · {formatCount(relationships.length)} relationship{relationships.length === 1 ? '' : 's'}</span>}
        <span className="data-grow" />
        <button className="data-btn is-primary" onClick={() => onOpenQuery?.('MATCH (n {project: $project})\nRETURN n LIMIT 50')} type="button">Query the graph</button>
      </div>
      {error && <ErrorState error={error} onRetry={load} />}
      {!error && !answer && <Loading label="Loading the graph…" />}
      {answer && !labels.length && <EmptyState title="No nodes yet">Nodes your services create for this project show here.</EmptyState>}
      {answer && labels.length > 0 && (
        <div className="data-split">
          <section aria-labelledby="data-labels-title" className="data-card data-grow-card">
            <h2 className="data-card-head" id="data-labels-title">Labels</h2>
            <ul className="data-plain-list">
              {labels.map((l) => (
                <li key={l.label}>
                  <button
                    aria-current={l.label === label ? 'true' : undefined}
                    className={`data-kv-btn${l.label === label ? ' is-on' : ''}`}
                    onClick={() => onOpenQuery?.(`MATCH (n:${quote(l.label)} {project: $project})\nRETURN n LIMIT 50`)}
                    type="button"
                  >
                    <span className="data-mono">:{l.label}</span>
                    <span className="data-muted">{formatCount(l.count)}</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
          <section aria-labelledby="data-rels-title" className="data-card data-grow-card">
            <h2 className="data-card-head" id="data-rels-title">Relationships</h2>
            {!relationships.length && <EmptyState title="No relationships yet" />}
            <ul className="data-plain-list">
              {relationships.map((r) => (
                <li key={r.type}>
                  <button
                    className="data-kv-btn"
                    onClick={() => onOpenQuery?.(`MATCH (a {project: $project})-[r:${quote(r.type)}]->(b {project: $project})\nRETURN a, r, b LIMIT 50`)}
                    type="button"
                  >
                    <span className="data-mono">[:{r.type}]</span>
                    <span className="data-muted">{formatCount(r.count)}</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        </div>
      )}
    </div>
  );
}

GraphView.propTypes = { label: PropTypes.string, onOpenQuery: PropTypes.func, projectId: PropTypes.string.isRequired };
